<template>
  <v-container fluid>
    <SharedTableSkeleton
      v-if="showSkeletonLoading"
      :row-count="pagination.perPage || 10"
      :column-count="4"
      column-template="2fr 1fr 1fr 80px"
    />
    <div v-if="!dataLoading" class="d-flex flex-column employee-card-path-to-readiness">
      <EmployeePageHeader
        :key="
          employeeData && employeeData.id
            ? employeeData.id +
              '-' +
              (employeeData.primary_positions ? employeeData.primary_positions.length : 0)
            : 'default'
        "
        :status="employeeStatus"
        :name="employeeFullName"
        :avatar="employeeData?.avatar || ''"
        :title="employeeData?.title || ''"
        :archived="isArchived"
        :tracking="isTracking"
        :readiness="employeeData?.current_readiness || 0"
        :last_month_readiness="lastMonthReadiness"
        :breadcrumbs="breadcrumbsList"
        :employeeId="employeeData?.id"
        :employeeData="employeeData"
        :readinessRecalc="{ pending: readinessIsPending, failed: readinessHasFailed }"
        @employeeUpdated="handleEmployeeUpdate"
        @employeeStatusChanged="handleEmployeeStatusChanged"
        @employeeDeleted="handleEmployeeDeleted"
      />
    </div>
    <div v-if="!dataLoading" class="custom-path">
      <div v-if="dataSource.length === 0 && employeeData">
        <NoRecordFound
          :title="''"
          :subtitle="''"
          :showSpecificMessage="title"
          message=""
          class="no-record-found"
          :shouldShowAddAction="true"
          :noBorder="true"
          :showActiveCounter="false"
          :showClearAction="false"
          :btnTitle="btnTitle"
          :addPrefix="false"
          :description="description"
          :plusIcon="false"
          @onAction="handleButtonClick"
        />
      </div>
      <PathToReadinessTreeList
        v-else
        ref="PathToReadinessTreeList"
        :key="treeListKey"
        :dataSource="dataSource"
        :topLevel="topLevel"
        :removeBorder="removeBorder"
        :dropdownItems="dropdownItems"
        @optionClick="onOptionClick"
        @treeExpandRole="onTreeExpandRole"
        :showRespBadge="true"
        @loadData="reloadDataAfterChanges()"
        @importanceChanged="onImportanceChanged"
        :loading="loading.initial"
        :treeMode="true"
        :showDropDown="false"
        :expandedKeys="expandedKeys"
        @onExpansionStateChange="handleExpansionStateChange"
        @loadDataAfterBadgeOperation="reloadDataAfterBadgeOperation()"
        :pendingReadinessIds="readinessPendingIds"
        :failedReadinessIds="readinessFailedIds"
      />
      <ExPagination
        v-if="dataSource.length > 0 && pagination.total > pagination.perPage"
        :pagination="pagination"
        @onRequest="handlePaginationRequest"
      />
    </div>
    <EmployeeModal
      :dialog="isForceBadge"
      :empName="employeeData ? `${employeeData.first_name} ${employeeData.last_name}` : ''"
      :employee="employeeDataWithBadges || employeeInfo"
      :responsibility="currentResponsibility"
      label="Responsibility"
      :name="currentResponsibility && currentResponsibility.name"
      :empForceBadgeClass="true"
      :hideEmployeePicker="true"
      :value="[1]"
      @back="onEmployeeModalBack"
      @onAction="onClickForceBadge"
      :showStep="2"
      @removeBadge="showRemoveBadge"
      :isArchitectWillingToCoach="actingUserArchitectWillCoach"
    />
    <EvalResponsibilityModal
      v-if="isInspectResModal"
      :dialog="isInspectResModal"
      :badges="badges"
      :checkpointsData="checkpointsData"
      :checkpointCounts="currentCheckpointCounts"
      :evalModalHeaderState="evalModalHeaderState"
      :evalModalEmpState="evalModalEmpState"
      @closeModal="isInspectResModal = false"
      :currentResponsibilityId="currentResponsibility && currentResponsibility.id"
      :assignmentData="false"
      @grantBadge="grantOrForceBadge"
      @reloadData="reloadCheckpointsData"
      @removeBadge="processRemoveBadgeFromEvalModal"
      @upgradeBadge="upgradeBadge"
      @reloadBadgeHolderItems="reloadDataAfterChanges"
      @showAwardBadge="showAwardBadge"
    />
    <ActivateEmployee
      :show="activateEmployee"
      :employee="currenntEmployee"
      @onClose="onCloseModal"
      @onActiveEmployee="handleActivateEmployee"
    ></ActivateEmployee>
    <ReActivateEmployee
      :show="reActivateEmployee"
      :employee="currenntEmployee"
      @onClose="onCloseModal"
      @onActiveEmployee="reactivateEmployee"
    ></ReActivateEmployee>

    <ExEmployeePrimaryAssignmentWizard
      label="Position"
      v-model="assignPosition"
      :items="primaryPositions.availablePositions"
      :currentEmployee="employeeData"
      @onAssignPosition="onAssignPositionToEmployee"
      :type="'scrollPositionPicker'"
      :isLoadingMore="primaryPositions.isLoadingMore"
      :hasMoreData="primaryPositions.hasMoreData"
      @load-more="loadMorePrimaryPositions"
      @search="searchPrimaryPositions"
      :errorMessage="errorMessage"
      :isAssigning="loading.employeeAssignment"
    />
    <RetireResponsibilityCatelog
      v-model="showRetireResponsibilityDialog"
      :responsibility="
        currentResponsibility && currentResponsibility.name ? currentResponsibility.name : ''
      "
      @onSave="retireResponsibility"
    />
    <BadgeConfirmationDialog
      :isVisible="showBadgeConfirmationDialog"
      :badgeData="badgeData"
      :badgeType="
        badgeData && badgeData.payload && badgeData.payload.issued_method == 'given'
          ? 'Give'
          : 'Award'
      "
      :responsibilityName="
        currentResponsibility && currentResponsibility.name ? currentResponsibility.name : ''
      "
      :employee="{
        name: `${employeeData && employeeData.first_name ? employeeData.first_name : ''} ${
          employeeData && employeeData.last_name ? employeeData.last_name : ''
        }`,
        avatar: employeeData && employeeData.photoUrl ? employeeData.photoUrl : '',
      }"
      :level="
        getBadgeLevel(
          badgeData && badgeData.payload && badgeData.payload.level ? badgeData.payload.level : null
        )
      "
      @close="showBadgeConfirmationDialog = false"
      :currentEmployee="$store.getters['employeeStore/currentEmployee']"
      @onClick="onClick"
    />
    <AwardBadgeDialog
      :employee="{
        name: `${
          evalModalEmpState && evalModalEmpState.firstName ? evalModalEmpState.firstName : ''
        }`,
        avatar: evalModalEmpState && evalModalEmpState.photoUrl ? evalModalEmpState.photoUrl : '',
      }"
      :level="awardBadgeLevel"
      :isVisible="showAwardBadgeModal"
      @skip="showAwardBadgeModal = false"
      @close="showAwardBadgeModal = false"
      @activate="awardBadge"
      :currentEmployee="$store.getters['employeeStore/currentEmployee']"
      :responsibilityName="(currentSelectRes && currentSelectRes.name) || ''"
    />
  </v-container>
</template>
<script>
import { normalizeBadgeLevel } from "@components/utils/normalizeBadgeLevel";
import AwardBadgeDialog from "@components/AwardBadgeDialog/AwardBadgeDialog.vue";
import NoRecordFound from "@components/NoRecordFound/NoRecordFound.vue";
import SharedTableSkeleton from "@components/SharedTableSkeleton/SharedTableSkeleton.vue";
import { LD_FLAG_IDEA_MDP_560 } from "@components/constants/launchDarklyFlagKeys";
import PathToReadinessTreeList from "@components/DataTable/PathToReadinessTreeList.vue";
import ExPagination from "@components/DataTable/ExPagination.vue";
import dropdownMixin from "@components/DropDownMenu/dropdownMixin";
import EmployeeModal from "@components/EmployeeModal/EmployeeModal.vue";
import EvalResponsibilityModal from "@components/EvalResponsibilityModal/EvalResponsibilityModal.vue";
import RetireResponsibilityCatelog from "@components/RetireResponsibilityCatelog/RetireResponsibilityCatelog.vue";
import {
  getListActiveCheckpointsEval,
  createProficiencyBadge,
  activateEmployee,
  reactivateEmployee,
  unArchiveEmployee,
  deleteResponsibilityBadge,
  updateRetireResponsibility,
  getRoleAssignment,
  getListofResponsibility,
  getEmployeeAssignments,
  addPositionAssignment,
  getResponsibilityAssignment,
  getAssignmentEval,
  getEmployeeBadges,
  getListOfPositionsForAssignment,
  // getResponsibilityChecklist,
} from "@/api";
import ExEmployeePrimaryAssignmentWizard from "@components/PositionPicker/ExEmployeePrimaryAssignmentWizard.vue";
import EmployeePageHeader from "@components/common/EmployeePageHeader.vue";
import ActivateEmployee from "@components/modals/ActiveEmployee.vue";
import ReActivateEmployee from "@components/modals/ReActivateEmployee.vue";
import { usePositionStore } from "@/store/pinia/position";
import { useEmployeeStore } from "@/store/pinia/employee";
import readinessRecalcMixin from "@/composables/readinessEmployees/readinessRecalcMixin";
import BadgeConfirmationDialog from "@components/BadgeConfirmationDialog/BadgeConfirmationDialog.vue";
import { useReadinessCatalogsStore } from "@/store/pinia/readinessCatalog";
import { fetchActingUserArchitectWillCoachForResponsibility } from "@components/utils/actingUserArchitectWillCoach";
export default {
  name: "EmployeePathToReadinessView",
  components: {
    PathToReadinessTreeList,
    EmployeePageHeader,
    EmployeeModal,
    EvalResponsibilityModal,
    NoRecordFound,
    SharedTableSkeleton,
    ActivateEmployee,
    ReActivateEmployee,
    ExEmployeePrimaryAssignmentWizard,
    RetireResponsibilityCatelog,
    BadgeConfirmationDialog,
    AwardBadgeDialog,
    ExPagination,
  },
  data() {
    return {
      dataSource: [],
      topLevel: "position",
      removeBorder: false,
      currentReadiness: null,
      currentSelectRes: null,
      currentResponsibility: null,
      isForceBadge: false,
      actingUserArchitectWillCoach: false,
      showEmployeeIssueBadge: false,
      isInspectResModal: false,
      allResponsibilities: [],
      checkpointsData: [],
      evalModalEmpState: null,
      evalModalHeaderState: null,
      activateEmployee: false,
      reActivateEmployee: false,
      unArchiveEmployee: false,
      assignPosition: false,
      currenntEmployee: null,
      errorMessage: "",
      positionStore: usePositionStore(),
      employeeStore: useEmployeeStore(),
      responsibilitiesToUseIds: [],
      showAwardBadgeModal: false,
      awardBadgeLevel: null,
      treeListKey: 0,
      badges: [
        {
          badge_id: "Apprentice",
          type: "Given",
        },
        {
          badge_id: "Professional",
          is_force: true,
        },
        {
          badge_id: "Coach",
        },
        {
          badge_id: "Master",
          is_force: false,
        },
      ],
      employeeData: null,
      employeeDataWithBadges: null,
      loading: {
        initial: false,
        readinessBar: false,
        responsibilities: {},
        assignments: false,
        employeeAssignment: false,
        forceBadge: false,
        employeeStatus: false,
        checkpoint: false,
      },
      primaryPositions: {
        availablePositions: [],
        isLoadingMore: false,
        currentPage: 1,
        totalCount: 0,
        hasMoreData: true,
        currentSearchTerm: "",
      },
      cachedAccountID: null,
      cachedEmpId: null,
      cachedAuthUser: null,
      showRetireResponsibilityDialog: false,
      showBadgeConfirmationDialog: false,
      badgeData: null,
      dataLoading: false,
      skeletonLoadingEnabled: false,
      readinessStore: useReadinessCatalogsStore(),
      pagination: {
        limit: 10,
        offset: 0,
        total: 0,
        perPage: 10,
        page: 1,
      },
      // Add expanded row loading states
      expandedRowLoadingStates: {},
      currentCheckpointCounts: {},
      availablePositions: [],
      // Store expanded tree state
      expandedKeys: [],
      // Store loaded child data to prevent duplicate API calls during expansion
      loadedChildData: new Map(),
    };
  },
  mixins: [dropdownMixin, readinessRecalcMixin],
  async mounted() {
    this.cachedAccountID = JSON.parse(localStorage.getItem("currentAccountID"));
    this.cachedAuthUser = JSON.parse(localStorage.getItem("user"));
    this.cachedEmpId = this.$route?.params?.id;
    this.$loader.show();
    this.dataLoading = true;
    this.$loader.hide();
    this.checkSkeletonFlag();
    const success = await this.initializeData();
    if (!success) {
      this.$router.replace({ name: "NotFound" });
      return;
    }
    this.dataLoading = false;
  },
  watch: {
    async storeEmployeeData(newEmployeeData, oldEmployeeData) {
      if (
        newEmployeeData &&
        newEmployeeData.current_status === "terminated" &&
        oldEmployeeData &&
        oldEmployeeData.current_status !== "terminated"
      ) {
        await this.reloadDataAfterChanges();
        await this.employeeStore.loadEmployees();
      }
    },
    "currentResponsibility.id": {
      handler() {
        this.loadActingUserArchitectWillCoach();
      },
    },
  },

  computed: {
    /** ER-10612 / MDP-560 — flag-gated table skeleton shown while the initial fetch is in flight. */
    showSkeletonLoading() {
      return this.dataLoading && this.skeletonLoadingEnabled;
    },
    statusConfig() {
      const status = this.$store.getters["employeeStore/currentEmployeeStatus"];
      const isArchived = this.$store.getters["employeeStore/isCurrentEmployeeArchived"];

      const statuses = {
        draft: {
          title: "Employee Not Yet Activated",
          description:
            "This employee is currently in draft status. Activate them to unlock the Path to readiness and start tracking their progress.",
          btnTitle: "Activate",
        },
        active: {
          title: "No Position Assigned Yet",
          description: "To track this employee's readiness, assign them to a position first.",
          btnTitle: "Assign",
        },
        terminated_unarchived: {
          title: "Employee Status: Terminated",
          description:
            "This employee's Path to readiness is unavailable because they have been terminated. Re-activate them to resume tracking their progress.",
          btnTitle: "Re-Activate",
        },
        terminated_archived: {
          title: "Employee Status: Archived",
          description:
            "This employee's Path to readiness is unavailable because they have been archived. To resume tracking their progress, unarchive and re-activate them.",
          btnTitle: "Re-Activate",
        },
      };

      if (status === "terminated") {
        return isArchived ? statuses.terminated_archived : statuses.terminated_unarchived;
      }

      return (
        statuses[status] || {
          title: "",
          description: "",
          btnTitle: "",
        }
      );
    },

    title() {
      return this.statusConfig.title;
    },

    description() {
      return this.statusConfig.description;
    },

    btnTitle() {
      return this.statusConfig.btnTitle;
    },
    employeeInfo() {
      return this.$store.getters["employeeStore/currentEmployee"];
    },
    storeEmployeeData() {
      return this.$store.getters["employeeStore/currentEmployee"];
    },
    employeeStatus() {
      const status = this.$store.getters["employeeStore/currentEmployeeStatus"];
      return status || this.employeeData?.current_status || "draft";
    },
    employeeFullName() {
      if (this.employeeData?.first_name && this.employeeData?.last_name) {
        return `${this.employeeData.first_name} ${this.employeeData.last_name}`;
      }
      return this.employeeData?.name || "Employee";
    },
    isArchived() {
      return (
        this.$store.getters["employeeStore/isCurrentEmployeeArchived"] ||
        this.employeeData?.archived ||
        false
      );
    },
    isTracking() {
      // Check if employee has tracking enabled (has positions assigned)
      return !!(this.employeeData?.primary_positions?.length > 0);
    },
    lastMonthReadiness() {
      // TODO: Get from backend when available
      return this.employeeData?.last_month_readiness || null;
    },
    breadcrumbsList() {
      const employeesHref = "/organization/employees";
      return [
        {
          text: "Employees",
          disabled: false,
          href: employeesHref,
        },
        {
          text: this.employeeFullName,
          disabled: true,
          truncate: true,
        },
      ];
    },
  },
  methods: {
    /** ER-10612 / MDP-560 — resolve the consistent-skeleton feature flag; off/failing -> legacy blank gap. */
    async checkSkeletonFlag() {
      try {
        this.skeletonLoadingEnabled = (await this.$getFlag?.(LD_FLAG_IDEA_MDP_560, false)) === true;
      } catch (error) {
        this.skeletonLoadingEnabled = false;
      }
    },
    async loadActingUserArchitectWillCoach() {
      const responsibilityId =
        this.currentResponsibility?.id || this.currentResponsibility?.responsibility_id;
      const responsibilityCode =
        this.currentResponsibility?.code || this.currentResponsibility?.code_str;
      this.actingUserArchitectWillCoach = await fetchActingUserArchitectWillCoachForResponsibility(
        responsibilityId,
        {
          responsibilityCode,
          accountId: this.cachedAccountID,
        }
      );
    },
    async fetchAvailablePositions(page = 1, searchTerm = "", isLoadMore = false) {
      const limit = 10;
      const offset = (page - 1) * limit;
      const name = searchTerm || "";
      try {
        this.primaryPositions.isLoadingMore = true;
        const response = await getListOfPositionsForAssignment({
          accountId: this.cachedAccountID,
          limit,
          offset,
          name,
        });
        const positions = response.data?.results || [];
        let newItems = positions.map((position) => ({
          id: position.id,
          code: position.code_str.match(/\d+/)[0],
          code_str: position.code_str,
          name: position.name,
          status: position.status,
        }));

        // Filter out positions if current employee has preparing_for_positions matching them
        if (this.employeeData?.preparing_for_positions?.length > 0) {
          const preparingCodeStr = this.employeeData.preparing_for_positions[0].code_str;
          newItems = newItems.filter((item) => item.code_str !== preparingCodeStr);
        }
        if (isLoadMore && this.primaryPositions.availablePositions) {
          this.primaryPositions.availablePositions = [
            ...this.primaryPositions.availablePositions,
            ...newItems,
          ];
        } else {
          // If it's initial load or search, replace items
          this.primaryPositions.availablePositions = newItems;
        }
        // Update pagination state
        this.primaryPositions.currentPage = page;
        this.primaryPositions.totalCount = response.data.count || 0;
        // hasMoreData should be true if there are more records beyond current page
        this.primaryPositions.hasMoreData = page * limit < this.primaryPositions.totalCount;
        this.primaryPositions.isLoadingMore = false;
      } catch (error) {
        console.error("Error fetching available positions:", error);
      }
    },
    async loadMorePrimaryPositions() {
      if (this.primaryPositions.isLoadingMore || !this.primaryPositions.hasMoreData) {
        return;
      }

      const nextPage = this.primaryPositions.currentPage + 1;
      await this.fetchAvailablePositions(nextPage, this.primaryPositions.currentSearchTerm, true);
    },

    // Method to handle search with pagination reset
    async searchPrimaryPositions(searchTerm) {
      this.primaryPositions.currentSearchTerm = searchTerm;
      this.primaryPositions.currentPage = 1;
      this.primaryPositions.hasMoreData = true;
      await this.fetchAvailablePositions(
        this.primaryPositions.currentPage,
        this.primaryPositions.currentSearchTerm,
        false
      );
    },
    async initializeData() {
      this.$loader.show();
      const empId = this.cachedEmpId || this.$route?.params?.id;
      const accountID =
        this.cachedAccountID || JSON.parse(localStorage.getItem("currentAccountID"));

      try {
        this.loading.readinessBar = true;

        // Get employee data from store (should be loaded by sidebar)
        let employeeData =
          this.employeeStore.currentEmployee ||
          this.$store.getters["employeeStore/currentEmployee"];

        // If employee data is not in store, try to fetch it
        if (!employeeData || !employeeData.id || employeeData.id != empId) {
          try {
            employeeData = await this.employeeStore.fetchCurrentEmployee(empId, true);
            if (employeeData) {
              this.$store.commit("employeeStore/setCurrentEmployee", employeeData);
            }
          } catch (error) {
            console.error("Error fetching employee data:", error);
            // If it's a 404 error, return false to trigger redirect
            if (error.response && error.response.status === 404) {
              return false;
            }
          }
        }

        // If employee data is still not available, return false
        if (!employeeData || !employeeData.id) {
          console.error("Employee data not found for ID:", empId);
          return false;
        }

        // Readiness calculation is handled by sidebar component on mount
        const [assignmentsResponse] = await Promise.all([
          getEmployeeAssignments(
            accountID,
            empId,
            this.pagination.limit,
            this.pagination.offset,
            "active"
          ),
        ]);

        if (employeeData) {
          this.employeeData = employeeData;
          this.currentReadiness = {
            current_readiness: employeeData.current_readiness,
          };
        }

        if (assignmentsResponse.data) {
          // Update pagination data
          this.pagination.total = assignmentsResponse.data.count || 0;

          if (assignmentsResponse.data.results?.length > 0) {
            this.dataSource = this.transformData(assignmentsResponse.data.results);
          } else {
            this.dataSource = [];
          }
        }

        // Force TreeList to re-render and reset expansion state for initial load
        this.treeListKey++;
        // Clear any previous expansion state for fresh start
        this.expandedKeys = [];
        // Clear child data cache for fresh start
        this.loadedChildData.clear();

        return true;
      } catch (error) {
        console.error("Error initializing data:", error);
        // Check if it's a 404 error
        if (error.response && error.response.status === 404) {
          return false;
        }
        return false;
      } finally {
        this.$loader.hide();
        this.loading.readinessBar = false;
      }
    },
    showAwardBadge(obj) {
      this.isInspectResModal = false;
      this.showAwardBadgeModal = true;
      // Convert level to lowercase and handle "Novice" case
      const level = normalizeBadgeLevel(obj?.level);
      if (level === "novice") {
        this.awardBadgeLevel = "apprentice"; // Default to apprentice for novice
      } else if (["apprentice", "proficient", "coach", "architect"].includes(level)) {
        this.awardBadgeLevel = level;
      } else {
        this.awardBadgeLevel = "apprentice"; // Default fallback
      }
      // Set the responsibility name if provided
      if (obj?.responsibilityName) {
        this.currentSelectRes = { name: obj.responsibilityName };
      }
    },
    async awardBadge(isNotify = false) {
      // Show main loader and keep it until everything is complete
      this.$loader.show();

      try {
        const routeParams = this.$route && this.$route.params;
        let responsibilityId = null;
        if (routeParams && routeParams.objId) {
          responsibilityId = routeParams.objId;
        } else if (this.currentResponsibility && this.currentResponsibility.id) {
          responsibilityId = this.currentResponsibility.id;
        }
        if (!responsibilityId) throw "no responsibility id available";
        const account = JSON.parse(localStorage.getItem("currentAccountID"));
        const data = {
          employee: this.evalModalEmpState.id,
          responsibility: responsibilityId,
          level: this.getBadgeLevelByLabel(this.awardBadgeLevel),
          given: false,
          issued_method: "awarded",
          notify: isNotify,
        };

        await createProficiencyBadge(account, data);
        const employeeData = await this.employeeStore.fetchCurrentEmployee(
          this.employeeInfo?.id,
          true
        );
        if (employeeData) {
          // Update store and local data with fresh employee status
          this.$store.commit("employeeStore/setCurrentEmployee", employeeData);
          this.employeeData = employeeData;
        }

        await this.reloadDataAfterChanges();

        // Clear modal states after everything is complete
        this.clearModalStates();
      } catch (e) {
        console.error("❌ Error in awardBadge:", e);
        this.clearModalStates(); // Clear even on error
      } finally {
        // Hide loader only after everything is complete
        this.$loader.hide();
      }
    },

    getBadgeLevelByLabel(level) {
      if (!level || typeof level !== "string") {
        return 0; // Default to novice if level is null, undefined, or not a string
      }
      switch (normalizeBadgeLevel(level)) {
        case "novice":
          return 0;
        case "apprentice":
          return 1;
        case "proficient":
          return 2;
        case "coach":
          return 3;
        case "architect":
          return 4;
        default:
          return 0;
      }
    },
    async upgradeBadge(upgradeData) {
      try {
        this.badgeData = { payload: upgradeData?.payload?.newBadge };
        await this.onClick(false);
      } catch (e) {
        console.log(e);
      }
    },
    handleButtonClick() {
      const { id, first_name, last_name, availability, avatar, title } = this.employeeInfo;
      const employeeObj = {
        id,
        firstName: first_name,
        lastName: last_name,
        availability,
        picture: avatar,
        title: title,
      };
      this.currenntEmployee = JSON.parse(JSON.stringify(employeeObj));
      if (this.employeeInfo.current_status === "draft") {
        this.activateEmployee = true;
      }
      if (
        this.employeeInfo.current_status === "terminated" &&
        this.employeeInfo.archived === false
      ) {
        this.reActivateEmployee = true;
      }
      if (
        this.employeeInfo.current_status === "terminated" &&
        this.employeeInfo.archived === true
      ) {
        this.reActivateEmployee = true;
        this.unArchiveEmployee = true;
      }
      if (this.employeeInfo.current_status === "active") {
        this.fetchAvailablePositions();
        this.assignPosition = true;
      }
    },
    async processRemoveBadgeFromEvalModal(data) {
      try {
        if (!data || !data.id) throw "badge id not found";

        // Show main loader and keep it until everything is complete
        this.$loader.show();

        await this.deleteBadge(data.id);

        await this.reloadDataAfterChanges();

        // Clear modal states after everything is complete
        this.clearModalStates();
      } catch (e) {
        console.error(e);
        this.clearModalStates(); // Clear even on error
      } finally {
        // Hide loader only after everything is complete
        this.$loader.hide();
      }
    },
    async deleteBadge(idBadge, reason = "", method = "") {
      try {
        const payload = {
          badge: idBadge,
          method: method || "string",
          reason: reason || "string",
        };
        const accountID = localStorage.getItem("currentAccountID");
        await deleteResponsibilityBadge(accountID, idBadge, payload);
        const employeeData = await this.employeeStore.fetchCurrentEmployee(
          this.employeeInfo?.id,
          true
        );
        if (employeeData) {
          // Update store and local data with fresh employee status
          this.$store.commit("employeeStore/setCurrentEmployee", employeeData);
          this.employeeData = employeeData;
        }
        // Employee data will be updated by sidebar component after this operation
      } catch (e) {
        console.error("Error removing badge: ", e);
      }
    },
    onCloseModal() {
      this.activateEmployee = false;
      this.reActivateEmployee = false;
      this.unArchiveEmployee = false;
      this.assignPosition = false;
      // Clear any error messages when closing modals
      this.errorMessage = "";
    },
    onEmployeeModalBack() {
      this.isForceBadge = false;
      this.employeeDataWithBadges = null; // Clear badge data when modal closes
    },

    clearModalStates() {
      // Clear all modal states to prevent unwanted reopening
      this.isInspectResModal = false;
      this.isForceBadge = false;
      this.showEmployeeIssueBadge = false;
      this.showBadgeConfirmationDialog = false;
      this.showAwardBadgeModal = false;
      this.employeeDataWithBadges = null;
      this.currentSelectRes = null;
      this.currentResponsibility = null;
      this.badgeData = null;
      this.evalModalEmpState = null;
      this.evalModalHeaderState = null;
    },

    dropdownItems(data) {
      const items = [];
      if (data && data.data && data.data.type === "responsibility" && !data.data.badge) {
        items.push({
          name: "Issue Badge",
          value: "issue_badge_action",
          icon: "plainminimalistic.svg",
          order: 2,
        });
      }
      if (data && data.data && data.data.type === "responsibility") {
        items.push({
          name: "Retire in Catalog",
          value: "retire_responsibility",
          icon: "icons/dropdown/activate.svg",
          order: 3,
        });
      }
      return items;
    },
    async reloadCheckpointsData() {
      await this.reloadDataAfterChanges();
    },
    async reloadDataAfterBadgeOperation() {
      this.$loader.show();
      // Immediately fetch fresh employee data from API to update status
      const employeeData = await this.employeeStore.fetchCurrentEmployee(
        this.employeeInfo?.id,
        true
      );
      if (employeeData) {
        // Update store and local data with fresh employee status
        this.$store.commit("employeeStore/setCurrentEmployee", employeeData);
        this.employeeData = employeeData;
      }
      await this.reloadDataAfterChanges();
      this.$loader.hide();
    },
    async grantOrForceBadge(badgeData) {
      this.isInspectResModal = false;
      this.showBadgeConfirmationDialog = true;
      this.badgeData = badgeData;
    },
    async onClick(isNotify = false) {
      // Show main loader and keep it until everything is complete
      this.$loader.show();

      try {
        const accountID =
          this.cachedAccountID || JSON.parse(localStorage.getItem("currentAccountID"));
        const newPayload = {
          level: this.badgeData.payload.level,
          employee: this.badgeData.payload.employee,
          given: this.badgeData.payload.given,
          responsibility: this.badgeData.payload.responsibility,
          issued_method: this.badgeData.payload.issued_method,
          notify: isNotify,
        };

        await createProficiencyBadge(accountID, newPayload);
        await this.reloadDataAfterChanges();
        const employeeData = await this.employeeStore.fetchCurrentEmployee(
          this.employeeInfo?.id,
          true
        );
        if (employeeData) {
          // Update store and local data with fresh employee status
          this.$store.commit("employeeStore/setCurrentEmployee", employeeData);
          this.employeeData = employeeData;
        }

        // Clear modal states after everything is complete
        this.clearModalStates();
      } catch (error) {
        console.error("❌ Error in onClick:", error);
        this.clearModalStates(); // Clear even on error
      } finally {
        // Hide loader only after everything is complete
        this.$loader.hide();
      }
    },
    async loadData() {
      const empId = this.cachedEmpId || this.$route?.params?.id;
      const accountID =
        this.cachedAccountID || JSON.parse(localStorage.getItem("currentAccountID"));
      this.$loader.show();

      try {
        await Promise.all([
          // this.getAuthUser(empId),
          this.getEmpAssignments(accountID, empId),
        ]);

        const responsibilitiesIdsNew = [];
        const uniqueRespIds = [...new Set(this.responsibilitiesToUseIds)];

        uniqueRespIds.forEach((respId) => {
          const respObj = this.allResponsibilities.find((el) => el.id == respId);
          if (respObj) {
            responsibilitiesIdsNew.push({
              id: respObj.id,
              code: respObj.code_str,
            });
          }
        });
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        this.$loader.hide();
      }
    },
    async loadAllCheckpointsEvaluationData(accountId, employeeId, responsibilityId) {
      const response = await getListActiveCheckpointsEval(accountId, responsibilityId, employeeId);
      return response.data;
    },
    async getEmpAssignments(accountId, empId) {
      const { data } = await getEmployeeAssignments(
        accountId,
        empId,
        this.pagination.limit,
        this.pagination.offset,
        "active"
      );

      if (data) {
        // Update pagination data
        this.pagination.total = data.count || 0;

        if (data.results?.length > 0) {
          this.dataSource = this.transformData(data.results);
        } else {
          this.dataSource = [];
        }
      }
    },
    async onEmployeeReadiness() {
      // Readiness calculation is now handled by the sidebar component
      const empId = this.cachedEmpId || this.$route?.params?.id;
      if (empId) {
        // Trigger readiness recalculation via sidebar
        this.$parent.$refs?.employeeSideBar?.recalculateEmployeeReadiness(empId);

        // Update local employee data from store
        const employeeData =
          this.employeeStore.currentEmployee ||
          this.$store.getters["employeeStore/currentEmployee"];
        if (employeeData) {
          this.employeeData = employeeData;
        }
      }
    },
    async reloadDataAfterChanges() {
      const empId = this.cachedEmpId || this.$route?.params?.id;
      const accountID =
        this.cachedAccountID || JSON.parse(localStorage.getItem("currentAccountID"));

      try {
        this.loading.readinessBar = true;

        // Preserve current expansion state before reloading
        this.preserveExpansionState();

        await this.getEmployeeData();
        // Trigger readiness recalculation via sidebar
        this.$parent.$refs?.employeeSideBar?.recalculateEmployeeReadiness(empId);

        // Update employee data from store
        const employeeData =
          this.employeeStore.currentEmployee ||
          this.$store.getters["employeeStore/currentEmployee"];
        if (employeeData) {
          this.employeeData = employeeData;
        }
        await this.getEmpAssignments(accountID, empId);

        // Refresh child data to show latest changes (like new badges)
        await this.refreshExpandedChildData(accountID, empId);

        // Force TreeList to re-render but preserve expansion state
        this.treeListKey++;

        // Restore expansion state after data is loaded
        // Use a combination of $nextTick and setTimeout for better timing
        this.$nextTick(() => {
          this.$nextTick(() => {
            this.restoreExpansionState();
          });
        });
      } catch (error) {
        console.error("Error reloading data:", error);
      } finally {
        this.loading.readinessBar = false;
      }
    },

    transformData(dataArray) {
      const result = [];
      const today = new Date();

      dataArray.forEach((data) => {
        // Transform position data
        const positionObject = this.transformPositionData(data, today);
        result.push(positionObject);

        // TODO: Later - handle role assignments when API is added
        // if (data.role_assignment_count > 0) {
        //   // Will call roles API and process role_assignments
        // }
      });

      // Sort the results by commitment level
      const commitmentOrder = {
        Primary: 1,
        "Preparing for": 2,
        Backup: 3,
      };

      result.sort((a, b) => {
        const aCommit = a.data?.commitmentLevel || "";
        const bCommit = b.data?.commitmentLevel || "";
        return (commitmentOrder[aCommit] || 99) - (commitmentOrder[bCommit] || 99);
      });

      return result;
    },

    transformPositionData(data, today) {
      const isNew =
        new Date(data.assigned_on).getTime() + 10 * 24 * 60 * 60 * 1000 > today.getTime();

      return {
        id: `position_${data.id}`, // Make position IDs unique
        parentId: -1, // Root-level has no parent
        data: {
          type: data.assignment_type,
          commitmentLevel: data.commitment_level,
          assignmentType: data.assignment_type,
          importanceLevel: data.importance,
          importanceSelector: true,
          totalSteps: 3,
          code: data.position_code,
          name: data.position_name,
          isNew: isNew,
          isAccepted: false,
          acceptanceStatus: "pending",
          // Add expandable indicator if roles exist
          hasRoles: data.role_assignment_count > 0,
          roleCount: data.role_assignment_count,
        },
        assignment_id: data.id,
        status: data.status,
        readiness: data.current_readiness,
      };
    },

    transformRoleData(role, parentId, today) {
      const isRoleNew =
        new Date(role.assigned_on).getTime() + 10 * 24 * 60 * 60 * 1000 > today.getTime();

      return {
        id: `role_${role.id}`, // Make role IDs unique
        parentId: parentId, // Set parent ID to main object
        data: {
          type: role.assignment_type,
          commitmentLevel: role.commitment_level,
          assignmentType: role.assignment_type,
          importanceLevel: role.importance,
          importanceSelector: true,
          totalSteps: 3,
          code: role.role_code,
          name: role.role_name,
          isNew: isRoleNew,
          isAccepted: isRoleNew,
          acceptanceStatus: "pending",
          assignment_id: parentId,
          // Add expandable indicator if responsibilities exist
          hasResponsibilities: role.responsibility_assignment_count > 0,
          responsibilityCount: role.responsibility_assignment_count,
        },
        assignment_id: role.id,
        readiness: role.current_readiness,
      };
    },

    transformResponsibilityData(resp, parentId, assignmentId, today) {
      let checkpoints = [];
      const isRespNew =
        new Date(resp.assigned_on).getTime() + 10 * 24 * 60 * 60 * 1000 > today.getTime();

      return {
        id: resp.id,
        parentId: parentId, // Correctly set parent ID
        data: {
          type: resp.assignment_type,
          commitmentLevel: resp.commitment_level,
          assignmentType: resp.assignment_type,
          importanceLevel: resp.importance,
          importanceSelector: true,
          totalSteps: 3,
          code: resp.responsibility_code,
          name: resp.responsibility_name,
          isNew: isRespNew,
          isAccepted: isRespNew,
          acceptanceStatus: "pending",
          assignment_id: assignmentId,
          badge: resp.badge,
          responsibility_id: 1310,
        },
        checkpointsData: checkpoints,
        status: resp.status,
        readiness: resp.current_readiness,
      };
    },

    transformResponsibilityDataFromAPI(resp, parentId, assignmentId, today) {
      const checkpoints = [];
      const isRespNew =
        new Date(resp.assigned_on).getTime() + 10 * 24 * 60 * 60 * 1000 > today.getTime();

      let badge = null;
      if (resp.badge_level !== null && resp.badge_status !== "expired") {
        badge = {
          id: resp.badge_id,
          level: resp.badge_level,
          status: resp.badge_issued_method,
          expiration_date: resp.badge_expiration_date,
          issued_method: resp.badge_issued_method,
        };
      }

      // Extract checkpoint counts from API response
      const checkpointCounts = {
        apprentice_checkpoint_count: resp.apprentice_checkpoint_count || 0,
        professional_checkpoint_count: resp.professional_checkpoint_count || 0,
        coach_checkpoint_count: resp.coach_checkpoint_count || 0,
        master_checkpoint_count: resp.master_checkpoint_count || 0,
        apprentice_passed_checkpoint_count: resp.apprentice_passed_checkpoint_count || 0,
        professional_passed_checkpoint_count: resp.professional_passed_checkpoint_count || 0,
        coach_passed_checkpoint_count: resp.coach_passed_checkpoint_count || 0,
        master_passed_checkpoint_count: resp.master_passed_checkpoint_count || 0,
      };

      return {
        id: `responsibility_${resp.id}`, // Make responsibility IDs unique
        parentId: parentId, // Correctly set parent ID
        data: {
          type: resp.assignment_type,
          commitmentLevel: resp.commitment_level,
          assignmentType: resp.assignment_type,
          importanceLevel: resp.importance,
          importanceSelector: true,
          totalSteps: 3,
          code: resp.responsibility_code,
          name: resp.responsibility_name,
          isNew: isRespNew,
          isAccepted: isRespNew,
          acceptanceStatus: "pending",
          assignment_id: assignmentId,
          badge: badge,
          responsibility_id: resp.responsibility,
          checkpointCounts: checkpointCounts,
        },
        checkpointsData: checkpoints,
        status: resp.status,
        readiness: resp.current_readiness,
      };
    },

    async getAllResponsibilities() {
      try {
        const accountID = this.cachedAccountID || localStorage.getItem("currentAccountID");
        const data = await getListofResponsibility(accountID);
        this.allResponsibilities = data.data.results;
      } catch (error) {
        console.error("Error fetching responsibilities:", error);
      }
    },
    async onTreeExpandRole({ assignmentId, rowType, expandedRowData }) {
      const accountID = window.localStorage.getItem("currentAccountID");
      const empId = this.$route.params.id;

      // Check if data is already loaded for this expansion
      const cacheKey = `${rowType}_${assignmentId}`;
      if (this.loadedChildData.has(cacheKey)) {
        return;
      }

      this.removeSkeletonLoaders(assignmentId);

      try {
        if (rowType === "position") {
          await this.handlePositionExpansion(accountID, empId, assignmentId);
          // Mark this position's roles as loaded
          this.loadedChildData.set(cacheKey, true);
        } else if (rowType === "role") {
          await this.handleRoleExpansion(accountID, empId, assignmentId, expandedRowData);
          // Mark this role's responsibilities as loaded
          this.loadedChildData.set(cacheKey, true);
        }
      } catch (error) {
        console.error("Error loading assignments:", error);
        this.removeSkeletonLoaders(assignmentId);
      }
    },

    async handlePositionExpansion(accountID, empId, positionAssignmentId) {
      const existingRoles = this.dataSource.filter(
        (item) =>
          item.parentId === `position_${positionAssignmentId}` &&
          item.data?.type === "role" &&
          !item.data?.isSkeletonLoader
      );

      if (existingRoles.length > 0) {
        return;
      }

      const positionData = this.dataSource.find(
        (item) => item.id === `position_${positionAssignmentId}`
      );
      const roleCount = positionData?.data?.roleCount || 2; // fallback to 2 if not found

      this.addSkeletonLoaders(`position_${positionAssignmentId}`, "position", roleCount);

      const response = await getRoleAssignment(accountID, empId, positionAssignmentId, "active");

      this.removeSkeletonLoaders(`position_${positionAssignmentId}`);

      if (response.data && response.data.results && response.data.results.length > 0) {
        const today = new Date();
        const roleData = response.data.results;

        const transformedRoles = roleData.map((role) =>
          this.transformRoleData(role, `position_${positionAssignmentId}`, today)
        );

        this.dataSource.push(...transformedRoles);

        const positionIndex = this.dataSource.findIndex(
          (item) => item.id === `position_${positionAssignmentId}`
        );
        if (positionIndex !== -1) {
          this.dataSource[positionIndex].data.hasRoles = true;
          this.dataSource[positionIndex].data.roleCount = transformedRoles.length;
        }
      }
    },

    async handleRoleExpansion(accountID, empId, roleAssignmentId, expandedRowData) {
      const existingResponsibilities = this.dataSource.filter(
        (item) =>
          item.parentId === expandedRowData.id &&
          item.data?.type === "responsibility" &&
          !item.data?.isSkeletonLoader
      );

      if (existingResponsibilities.length > 0) {
        return;
      }

      const responsibilityCount = expandedRowData.data?.responsibilityCount || 3; // fallback to 3 if not found

      this.addSkeletonLoaders(expandedRowData.id, "role", responsibilityCount);

      const response = await getResponsibilityAssignment(
        accountID,
        roleAssignmentId,
        "active",
        "active"
      );

      this.removeSkeletonLoaders(expandedRowData.id);

      if (response.data && response.data.results && response.data.results.length > 0) {
        const today = new Date();
        const responsibilityData = response.data.results;

        const transformedResponsibilities = responsibilityData.map((resp) =>
          this.transformResponsibilityDataFromAPI(resp, expandedRowData.id, roleAssignmentId, today)
        );

        this.dataSource.push(...transformedResponsibilities);

        // Update the parent role to show it has responsibilities
        const roleIndex = this.dataSource.findIndex((item) => item.id === expandedRowData.id);
        if (roleIndex !== -1) {
          this.dataSource[roleIndex].data.hasResponsibilities = true;
          this.dataSource[roleIndex].data.responsibilityCount = transformedResponsibilities.length;
        }
      }
    },
    onOptionClick(item) {
      if (item?.item) {
        switch (item.item.value) {
          case "issue_badge_action":
            this.onIssueBadge(item);
            break;
          case "retire_responsibility":
            this.showRetireResponsibilityDialog = true;
            this.currentResponsibility = item.data.data;
            break;
          default:
            break;
        }
      } else {
        this.onIssueBadge(item);
      }
    },
    async retireResponsibility() {
      this.$loader.show();
      try {
        const id = this.currentResponsibility?.responsibility_id;
        const name = this.currentResponsibility?.name;

        const account_id =
          this.cachedAccountID || JSON.parse(localStorage.getItem("currentAccountID"));
        if (!id || !name) {
          throw "data missing error";
        }
        const payload = {
          status: "retired",
          name: this.currentResponsibility?.name,
          account: account_id,
        };
        await updateRetireResponsibility(account_id, id, payload);
        this.showRetireResponsibilityDialog = false;

        // Use reloadDataAfterChanges instead of loadData to properly clear cache
        // and refresh tree expansion state
        // Immediately fetch fresh employee data from API to update status
        const employeeData = await this.employeeStore.fetchCurrentEmployee(
          this.employeeInfo?.id,
          true
        );
        if (employeeData) {
          // Update store and local data with fresh employee status
          this.$store.commit("employeeStore/setCurrentEmployee", employeeData);
          this.employeeData = employeeData;
        }
        await this.reloadDataAfterChanges();
      } catch (e) {
        console.log("error: ", e);
      } finally {
        this.$loader.hide();
      }
    },
    async onIssueBadge(item) {
      this.$loader.show();
      const emp_id = this.$route.params.id;

      const [response, , badgesResponse] = await Promise.all([
        getListActiveCheckpointsEval(
          this.cachedAccountID,
          item.data.data.responsibility_id,
          emp_id
        ),
        getAssignmentEval(this.cachedAccountID, item.data.data.responsibility_id, "responsibility"),
        getEmployeeBadges(this.cachedAccountID, emp_id),
      ]);

      this.$loader.hide();

      // Assign badges data to the employee object for the modal
      if (badgesResponse?.data?.results) {
        // Get current employee data
        const currentEmployeeData = this.$store.getters["employeeStore/currentEmployee"];
        if (currentEmployeeData) {
          // Create a copy and add badges
          this.employeeDataWithBadges = {
            ...currentEmployeeData,
            badges: badgesResponse.data.results,
            badgeList: badgesResponse.data.results,
          };
        }
      }

      this.checkpointsData = response?.data?.map((el) => {
        const newEl = {};
        newEl.id = el.id;
        newEl.status = el.status;
        newEl.proficiency_level = el.proficiency_level;
        newEl.level = el.proficiency_level;
        newEl.level_name = this.getProficiencyLevelNameFromNumber(el);
        newEl.criteria = el.criteria;
        newEl.instructions = el.instructions;
        newEl.evaluation_status = el.evaluation_status;
        newEl.change_evaluation_status = this.updateChangeEvaluationStatus(el);
        newEl.updated_on = el.updated_on;
        newEl.updated_by = el.updated_by;
        newEl.responsibility = el.responsibility;
        newEl.failed_reason = el.evaluation_reason;
        newEl.reset_reason = el.reset_reason;
        newEl.reset_by = el.reset_by;
        newEl.reset_on = el.reset_on;
        return newEl;
      });

      let currentEmployee = null;
      let currentEmployeeData = null;
      this.currentSelectRes = item;

      this.currentResponsibility = {
        id: item.data.data.responsibility_id,
        name: item.data?.data?.name,
        code_str: item.data?.data?.code,
        code: this.extractCode(item.data?.data?.code),
      };

      // Extract checkpoint counts from the clicked item
      this.currentCheckpointCounts = item.data?.data?.checkpointCounts || {
        apprentice_checkpoint_count: 0,
        professional_checkpoint_count: 0,
        coach_checkpoint_count: 0,
        master_checkpoint_count: 0,
        apprentice_passed_checkpoint_count: 0,
        professional_passed_checkpoint_count: 0,
        coach_passed_checkpoint_count: 0,
        master_passed_checkpoint_count: 0,
      };

      const hasActiveStatus = this.checkpointsData.some((item) => item.status === "active");

      if (hasActiveStatus) {
        this.showEmployeeIssueBadge = false;
        this.isForceBadge = false;
        // ER-11331: route into the newer coach inspection screen instead of the
        // legacy Checkpoint/Status table (EvalResponsibilityModal/CheckpointsTable).
        this.$router.push({
          path: "/your-stuff/coach",
          query: {
            employeeId: emp_id,
            responsibilityId: item.data.data.responsibility_id,
          },
        });
      } else {
        // Set up employee data with badge information for force badge scenario
        const exlAppData = JSON.parse(localStorage.getItem("exlAppData"));
        currentEmployeeData = this.$store.getters["employeeStore/currentEmployee"];
        const employees = exlAppData?.employees;

        if (Array.isArray(employees)) {
          currentEmployee = employees.find((e) => e.id === emp_id);
        } else {
          console.error("Employees data is not an array.");
        }
        const badgeList = this.getBadgesFromDataSource(this.dataSource);

        if (currentEmployee) {
          this.evalModalEmpState = {
            id: currentEmployee.id,
            firstName: currentEmployeeData.first_name,
            lastName: currentEmployeeData.last_name,
            position: currentEmployee.title,
            photoUrl: currentEmployeeData.avatar,
            availability: 1,
            isAllocated: false,
            badgeList,
            currentEmployeeBadge: item?.data?.data?.badge || null,
            access: currentEmployeeData.access || currentEmployee.access || { is_suspended: false },
          };
        } else {
          console.error("No employee found with ID:", emp_id);
        }

        this.showEmployeeIssueBadge = false;
        this.isInspectResModal = false;
        this.isForceBadge = true;
      }
      this.$loader.setAppLoading(false);
    },
    getProficiencyLevelNameFromNumber(el) {
      let levelName = "";
      if (el.proficiency_level === 1) {
        levelName = "Apprentice";
      } else if (el.proficiency_level === 2) {
        levelName = "Professional";
      } else if (el.proficiency_level === 3) {
        levelName = "Coach";
      } else if (el.proficiency_level === 4) {
        levelName = "Master";
      } else {
        levelName = "Unknown"; // Handle unexpected values
      }
      return levelName;
    },
    updateChangeEvaluationStatus(data) {
      if (data.evaluation_status == "not_applicable") {
        return "Not Applicable";
      } else if (data.evaluation_status == "passed") {
        return "Passed";
      } else if (data.evaluation_status == "failed") {
        return "Failed";
      } else if (data.evaluation_status == "reset") {
        return "Reset Pending Evaluation";
      } else {
        return "Pending Evaluation";
      }
    },
    async onClickForceBadge(item) {
      const responsibilityId = this.currentResponsibility && this.currentResponsibility.id;
      this.loading.forceBadge = true;

      // Show main loader and keep it until everything is complete

      try {
        const emp_id = localStorage.getItem("emp_id");
        const accountID = this.cachedAccountID || localStorage.getItem("currentAccountID");
        const payload = {
          employee: emp_id,
          responsibility: responsibilityId,
          level: item.radioVal,
          forced: true,
        };
        await createProficiencyBadge(accountID, payload);
        await this.reloadDataAfterBadgeOperation();

        this.clearModalStates();
      } catch (error) {
        console.error("Error issuing badge:", error);
        this.clearModalStates(); // Clear even on error
      } finally {
        this.loading.forceBadge = false;
        this.$loader.hide();
      }
    },
    updateResponsibilityBadge(responsibilityId, badgeLevel) {
      const respIndex = this.dataSource.findIndex(
        (item) =>
          item.data &&
          item.data.type === "responsibility" &&
          item.data.code === this.currentResponsibility?.code_str
      );

      if (respIndex > -1) {
        const badgeInfo = {
          id: Date.now(),
          issued_to: localStorage.getItem("emp_id"),
          is_current: true,
          level: badgeLevel,
        };
        const updatedItem = {
          ...this.dataSource[respIndex],
          data: {
            ...this.dataSource[respIndex].data,
            badge: badgeInfo,
          },
        };
        this.$set(this.dataSource, respIndex, updatedItem);
      }
    },
    getBadgesFromDataSource(dataSource) {
      const badgeList = [];
      dataSource.forEach((item) => {
        if (item.data && item.data.badge && item.data.type === "responsibility") {
          const badge = item.data.badge;
          const newBadge = {
            id: badge.id,
            issued_to: badge.issued_to,
            is_current: badge.is_current,
            level: badge.level,
            issued_for: {
              code: item.data.code, // Include the responsibility code for badge matching
            },
          };
          badgeList.push(newBadge);
        }
      });
      return badgeList;
    },
    async handleActivateEmployee() {
      this.$loader.setAppLoading(true);
      try {
        const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
        await activateEmployee(accountID, this.employeeInfo?.id);
        // Immediately fetch fresh employee data from API to get updated status
        const employeeData = await this.employeeStore.fetchCurrentEmployee(
          this.employeeInfo?.id,
          true
        );
        if (employeeData) {
          // Update store and local data with fresh employee status
          this.$store.commit("employeeStore/setCurrentEmployee", employeeData);
          this.employeeData = employeeData;
        }

        this.onCloseModal();
        await this.reloadDataAfterChanges();
      } catch (e) {
        console.error("Error activating employee:", e);
      } finally {
        this.$loader.setAppLoading(false);
      }
    },
    async reactivateEmployee() {
      try {
        this.$loader.setAppLoading(true);
        const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
        if (this.unArchiveEmployee) {
          await unArchiveEmployee(accountID, this.employeeInfo?.id);
        }
        await reactivateEmployee(accountID, this.employeeInfo?.id);
        // Immediately fetch fresh employee data from API to update status
        const employeeData = await this.employeeStore.fetchCurrentEmployee(
          this.employeeInfo?.id,
          true
        );
        if (employeeData) {
          // Update store and local data with fresh employee status
          this.$store.commit("employeeStore/setCurrentEmployee", employeeData);
          this.employeeData = employeeData;
        }

        this.onCloseModal();
        await this.reloadDataAfterChanges();
        this.$loader.setAppLoading(false);
      } catch (e) {
        this.$loader.setAppLoading(false);
        console.log("e: ", e);
      }
    },
    async onAssignPositionToEmployee(item) {
      const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
      this.loading.employeeAssignment = true;
      this.$loader.setAppLoading(true);
      // Clear any previous error messages
      this.errorMessage = "";
      if (!this.employeeData?.id || !item?.id) {
        this.$loader.setAppLoading(false);
        return;
      }
      try {
        const payload = {
          employee: this.employeeData.id,
          position: item.id,
          commitment: 1,
          allocation: 1,
          effective_on: item.effective_on,
        };
        await addPositionAssignment(accountID, payload);
        // Employee data will be updated by sidebar component after position assignment
        await this.reloadDataAfterChanges();
        this.onCloseModal();
      } catch (e) {
        console.error("error: ", e);
        // Handle API error messages
        if (e.response && e.response.status === 400 && e.response.data) {
          // Check if error response is an array (as mentioned in the user's case)
          if (Array.isArray(e.response.data) && e.response.data.length > 0) {
            this.errorMessage = e.response.data[0];
            this.$toast.error(e.response.data[0]);
          } else if (typeof e.response.data === "string") {
            this.errorMessage = e.response.data;
            this.$toast.error(e.response.data);
          } else if (e.response.data.message) {
            this.errorMessage = e.response.data.message;
            this.$toast.error(e.response.data.message);
          } else {
            this.errorMessage = "An error occurred while assigning the position.";
            this.$toast.error("An error occurred while assigning the position.");
          }
        } else {
          this.errorMessage = "An error occurred while assigning the position.";
          this.$toast.error("An error occurred while assigning the position.");
        }
      } finally {
        this.$loader.setAppLoading(false);
        this.loading.employeeAssignment = false;
      }
    },
    extractCode(str) {
      let match = str?.match(/#\d+/);
      return match ? match[0] : null;
    },
    async getEmployeeData() {
      try {
        // Get employee data from store (updated by sidebar component)
        const data =
          this.employeeStore.currentEmployee ||
          this.$store.getters["employeeStore/currentEmployee"];
        if (data) {
          this.employeeData = data;
        }
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    },
    async handleEmployeeUpdate() {
      await this.getEmployeeData();
      await this.reloadDataAfterChanges();
      this.$emit("employeeUpdated");
    },
    async handleEmployeeStatusChanged() {
      await this.getEmployeeData();
      await this.reloadDataAfterChanges();
      this.$emit("employeeStatusChanged");
    },
    async handleEmployeeDeleted() {
      // Redirect to employees list when employee is deleted
      this.$router.push({ name: "Employees" });
    },
    async handleRefreshEmployeeData() {
      // Force a complete refresh of employee data from API
      return await this.initializeData();
    },
    async showRemoveBadge(item) {
      this.$refs.PathToReadinessTreeList.showRemoveBadge(item);
    },

    onImportanceChanged(event) {
      // Update local data without full reload to preserve tree state
      const item = this.dataSource.find((item) => item.id === event.itemId);
      if (item && item.data) {
        item.data.importanceLevel = event.newImportance;

        if (this.skeletonLoadingEnabled) {
          this.startReadinessRecalcRefresh(event.itemId);
          return;
        }

        // Update readiness bar without full reload
        this.onEmployeeReadiness();
      }
    },

    // Helper method to extract original ID from prefixed ID
    getOriginalId(prefixedId) {
      if (typeof prefixedId === "string") {
        if (prefixedId.startsWith("position_")) return prefixedId.replace("position_", "");
        if (prefixedId.startsWith("role_")) return prefixedId.replace("role_", "");
        if (prefixedId.startsWith("responsibility_"))
          return prefixedId.replace("responsibility_", "");
      }
      return prefixedId;
    },
    getBadgeLevel(level) {
      switch (level) {
        case 1:
          return "Apprentice";
        case 2:
          return "Professional";
        case 3:
          return "Coach";
        case 4:
          return "Master";
        default:
          return "Unknown";
      }
    },
    async handlePaginationRequest(paginationData) {
      // Calculate offset from page and limit
      this.pagination.page = paginationData.page;
      this.pagination.offset = (paginationData.page - 1) * this.pagination.limit;
      this.$loader.show();
      try {
        // Preserve current expansion state before pagination
        this.preserveExpansionState();

        const empId = this.cachedEmpId || this.$route?.params?.id;
        const accountID =
          this.cachedAccountID || JSON.parse(localStorage.getItem("currentAccountID"));
        await this.getEmpAssignments(accountID, empId);

        // Refresh child data to show latest changes
        await this.refreshExpandedChildData(accountID, empId);

        // Force TreeList to re-render but preserve expansion state
        this.treeListKey++;

        // Restore expansion state after data is loaded
        // Use a combination of $nextTick and setTimeout for better timing
        this.$nextTick(() => {
          this.$nextTick(() => {
            this.restoreExpansionState();
          });
        });
      } catch (error) {
        console.error("Error loading paginated data:", error);
      } finally {
        this.$loader.hide();
      }
    },

    addSkeletonLoaders(parentId, rowType, count = null) {
      // Check if skeleton loaders already exist for this parent
      const existingSkeletons = this.dataSource.filter(
        (item) => item.parentId === parentId && item.data?.isSkeletonLoader
      );

      if (existingSkeletons.length > 0) {
        return; // Skip adding if already exists
      }

      // Use provided count or fallback to default values
      const skeletonCount = count || (rowType === "position" ? 2 : 3);
      const skeletonItems = [];

      for (let i = 0; i < skeletonCount; i++) {
        const skeletonId = `skeleton-${parentId}-${i}`;
        skeletonItems.push({
          id: skeletonId,
          parentId: parentId,
          data: {
            type: "skeleton",
            assignmentType: rowType === "position" ? "role" : "responsibility",
            skeletonFor: rowType,
            isSkeletonLoader: true,
            commitmentLevel: "",
            importanceLevel: 0,
            importanceSelector: false,
            totalSteps: 3,
            code: "",
            name: "",
            isNew: false,
            isAccepted: false,
            acceptanceStatus: "pending",
          },
          readiness: 0,
          status: "loading",
        });
      }

      // Add skeleton items to dataSource
      this.dataSource = [...this.dataSource, ...skeletonItems];
    },

    removeSkeletonLoaders(parentId) {
      // Remove skeleton items for this parent
      this.dataSource = this.dataSource.filter((item) => {
        const isSkeletonForParent = item.id.toString().startsWith(`skeleton-${parentId}-`);
        const isSkeletonLoader = item.data?.isSkeletonLoader;
        const hasCorrectParent = item.parentId === parentId;

        // Remove if it's a skeleton loader for this parent
        return !(isSkeletonForParent && isSkeletonLoader && hasCorrectParent);
      });
    },

    // Expansion state management methods
    preserveExpansionState() {
      try {
        const treeListRef = this.$refs?.PathToReadinessTreeList?.$refs?.treelist;
        if (treeListRef && treeListRef.instance) {
          const treeInstance = treeListRef.instance;
          const currentExpandedKeys = treeInstance.option("expandedRowKeys") || [];
          this.expandedKeys = [...currentExpandedKeys];
        } else {
          console.warn("TreeList reference not found during preserve");
          // Keep existing expanded keys if available
        }
      } catch (error) {
        console.warn("Could not preserve expansion state:", error);
        // Don't reset expandedKeys here, keep what we have
      }
    },

    restoreExpansionState() {
      try {
        if (this.expandedKeys.length > 0) {
          // Force reactivity by creating a new array reference
          this.expandedKeys = [...this.expandedKeys];
        }
      } catch (error) {
        console.warn("Could not restore expansion state:", error);
      }
    },

    handleExpansionStateChange(expandedKeys) {
      // Update our local copy of expanded keys when user manually expands/collapses
      this.expandedKeys = expandedKeys || [];
    },

    async refreshExpandedChildData(accountID, empId) {
      // Get all expanded keys to know what child data to refresh
      const expandedKeys = this.expandedKeys || [];
      if (expandedKeys.length === 0) {
        return;
      }

      // Show loading state for readiness bar during refresh
      this.loading.readinessBar = true;

      try {
        // Clear the cache so we can reload fresh data
        this.loadedChildData.clear();

        // Remove all child items from dataSource so they can be reloaded with fresh data
        this.dataSource = this.dataSource.filter((item) => item.parentId === -1);

        // Reload child data for each expanded item
        for (const expandedKey of expandedKeys) {
          const expandedItem = this.dataSource.find((item) => item.id === expandedKey);
          if (!expandedItem) continue;

          try {
            if (expandedItem.data?.type === "position") {
              // Get the numeric ID from the prefixed ID
              const positionAssignmentId = expandedKey.replace("position_", "");
              await this.handlePositionExpansion(accountID, empId, positionAssignmentId);
            } else if (expandedItem.data?.type === "role") {
              // Get the numeric ID from the prefixed ID
              const roleAssignmentId = expandedKey.replace("role_", "");
              await this.handleRoleExpansion(accountID, empId, roleAssignmentId, expandedItem);
            }
          } catch (error) {
            console.error(`Error refreshing data for ${expandedKey}:`, error);
          }
        }
      } finally {
        this.loading.readinessBar = false;
      }
    },
  },
};
</script>
<style>
.custom-path-readiness {
  border: 1px solid #eaecf0;
  box-shadow: 0px 1px 2px 0px #1018280d;
  border-radius: 8px;
  padding: 15px;
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}
.readiness-title {
  font-family: Inter;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  text-align: left;
  color: #344054;
}
.custom-readiness {
  width: 100%;
  margin-left: 15px;
}
.custom-readiness .progress-bar-container {
  width: 100%;
}
.custom-readiness .progress-value {
  font-weight: 600;
}
.custom-path {
  border: 1px solid #eaecf0;
  box-shadow: 0px 1px 2px 0px #1018280d;
  border-radius: 8px;
}
</style>
